import { type FC, memo, useCallback } from "react";
import { ButtonKind, ButtonSize, ButtonVariant } from "@stubhub/broadway-web";
import type { EventListGridItem } from "src/http/api-types";
import { GridType } from "src/http/types";
import { useEventAnalytics } from "src/website-apps/shared/components/common-event-grid-list-item/use-event-analytics";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { chipRootStyle, eventItemStyle } from "./PerformerDateCard.styles";
import { ResidencyShowtimeButton } from "./ResidencyShowtimeButton";

interface Props {
  event: EventListGridItem;
  calendarIndex: number;
  onClick: (event: EventListGridItem) => void;
  onMouseEnter: (event: EventListGridItem) => void;
  openNotificationHandler: (eventId: number) => void;
}

export const ResidencyEventItem: FC<Props> = memo(
  ({ event, calendarIndex, onClick, onMouseEnter, openNotificationHandler }) => {
    const [state] = useEventsGridContext();
    const backendAnalyticsId = state.calendarEvents?.pagedEvents?.metadata?.backendAnalyticsId ?? null;
    const { fireClickPageEvents, fireHoverPageEvents, fireInViewPageEvents } = useEventAnalytics({
      event,
      gridType: GridType.Residency,
      index: calendarIndex,
      isHighlightedItem: false,
      merchTags: [],
      enableInViewEvents: true,
      backendAnalyticsIdOverride: backendAnalyticsId,
    });

    const cvm = event.calendarViewModel;
    const isTba = cvm?.isTimeConfirmed === false;
    const isSoldOut = !isTba && cvm?.hasActiveListings === false;
    const time = isTba ? "" : (cvm?.time ?? "");
    const price = isSoldOut ? null : (cvm?.contextSensitiveFormattedMinPrice ?? null);
    const handleInView = useCallback(() => {
      fireInViewPageEvents();
    }, [fireInViewPageEvents]);

    const handleClick = useCallback(() => {
      onClick(event);
      fireClickPageEvents();
    }, [onClick, event, fireClickPageEvents]);

    const handleMouseEnter = useCallback(() => {
      onMouseEnter(event);
      fireHoverPageEvents();
    }, [onMouseEnter, event, fireHoverPageEvents]);

    return (
      <div className={eventItemStyle} data-calendar-index={calendarIndex} onMouseEnter={handleMouseEnter}>
        <InViewTracker onInView={handleInView} />
        <ResidencyShowtimeButton
          classes={{ root: chipRootStyle }}
          kind={ButtonKind.Primary}
          isTba={isTba}
          isSoldOut={isSoldOut}
          onClick={handleClick}
          price={price}
          showSoldOutText
          size={ButtonSize.Default}
          time={time}
          variant={isSoldOut || isTba ? ButtonVariant.Regular : ButtonVariant.Outline}
          {...(event.eventId ? { onSoldOutClick: () => openNotificationHandler(event.eventId!) } : {})}
          {...(event.name ? { "aria-label": event.name } : {})}
          {...(!isTba && !isSoldOut && event.url ? { href: event.url } : {})}
        />
      </div>
    );
  },
);

ResidencyEventItem.displayName = "ResidencyEventItem";
